import { CanonicalVenueFacts } from "@/pages/discover/PublicVenueFacts";
import { useArtistScope } from "@/lib/artistContext";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import PageHeader from "@/components/PageHeader";
import { CRMInsightsPanel } from "@/components/crm/CRMInsightsPanel";
import { CRMEpisodesList } from "@/components/memory/CRMEpisodesList";
import CapabilityGate from "@/components/CapabilityGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CodeBlock } from "@/components/ui/code-block";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { fetchVenueDetail } from "@/api/venues";
import { digestCrmVenue } from "@/api/crm";
import { usePageBreadcrumbs } from "@/providers/BreadcrumbProvider";
import { useToast } from "@/components/ToastProvider";

type VenueDetail = {
  id: string;
  venue?: string;
  company_name?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  capacity?: number | null;
  tags?: string[];
  social_links?: string[];
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  metadata?: Record<string, unknown> | null;
};

export default function VenueDetailPage() {
  const { selectedArtistId } = useArtistScope();
  const { venueId } = useParams<{ venueId: string }>();
  const [venue, setVenue] = useState<VenueDetail | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "episodes">("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [digesting, setDigesting] = useState(false);
  const { push } = useToast();

  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    setLoading(true);
    fetchVenueDetail(venueId)
      .then((payload) => {
        if (!cancelled) setVenue(payload);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unable to load venue.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  const breadcrumbs = useMemo(
    () => [
      { label: "Contacts", href: "/contacts" },
      { label: "Venues", href: "/venue-map" },
      { label: venue?.venue ?? `Venue ${venueId ?? ""}` },
    ],
    [venue?.venue, venueId],
  );
  usePageBreadcrumbs(breadcrumbs);

  const handleDigestVenue = async () => {
    if (!venueId) {
      push({ title: "Missing venue", message: "Venue identifier is required." });
      return;
    }
    const numericId = Number(venueId);
    if (Number.isNaN(numericId)) {
      push({ title: "Invalid venue", message: "Venue identifier is invalid." });
      return;
    }
    setDigesting(true);
    try {
      await digestCrmVenue(numericId);
      push({ title: "Venue digest", message: "Bandsintown digest queued for this venue." });
    } catch (err) {
      push({
        title: "Digest failed",
        message: err instanceof Error ? err.message : "Unable to digest venue.",
      });
    } finally {
      setDigesting(false);
    }
  };

  return (
    <div className="aa-page-shell space-y-6">
      <PageHeader
        title={venue?.venue ?? "Venue details"}
        description="Cortex-generated venue suitability and recent engagement history."
        actions={
          <CapabilityGate flag="venue_digest" variant="hide">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleDigestVenue()}
              disabled={digesting}
            >
              {digesting ? "Digesting…" : "Digest venue"}
            </Button>
          </CapabilityGate>
        }
      />
      {error && <p className="text-xs text-rose-400">{error}</p>}

      <ResizablePanelGroup direction="horizontal" className="aa-glass-card min-h-[400px] rounded-2xl">
        <ResizablePanel defaultSize={65}>
          <div className="p-4">
            <div className="mb-4 inline-flex rounded-full border border-border/60 bg-background/50 p-1 text-xs">
              <button
                type="button"
                onClick={() => setActiveTab("overview")}
                className={`rounded-full px-3 py-1 ${activeTab === "overview" ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("episodes")}
                className={`rounded-full px-3 py-1 ${activeTab === "episodes" ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Episodes
              </button>
            </div>

            {activeTab === "overview" ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Location & contacts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CanonicalVenueFacts globalVenueId={typeof venue?.metadata?.global_venue_id === "string" ? venue.metadata.global_venue_id : null} artistId={selectedArtistId} />
                    <dl className="grid gap-3 text-sm md:grid-cols-2">
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Company</dt>
                        <dd className="text-foreground">{venue?.company_name ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">City</dt>
                        <dd className="text-foreground">
                          {venue?.city}, {venue?.state} {venue?.country}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Capacity</dt>
                        <dd className="text-foreground">{venue?.capacity ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Contact</dt>
                        <dd className="text-foreground">
                          {venue?.email ?? "—"} · {venue?.phone ?? "—"}
                        </dd>
                      </div>
                    </dl>
                    {venue?.tags?.length ? (
                      <div className="mt-4 flex flex-wrap gap-2 text-xs">
                        {venue.tags.map((tag) => (
                          <span key={tag} className="rounded-full bg-background/50 px-3 py-1 text-foreground/80">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
                {venue?.metadata && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Metadata</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CodeBlock value={JSON.stringify(venue.metadata, null, 2)} language="json" />
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Venue episodes</CardTitle>
                </CardHeader>
                <CardContent>
                  <CRMEpisodesList entityType="venue" entityId={venueId} />
                </CardContent>
              </Card>
            )}
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={35} className="border-l border-border/40">
          <CRMInsightsPanel objectType="venue" objectId={venueId} objectName={venue?.venue ?? undefined} />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
